/**
 * ChatInputArea.tsx
 *
 * Chat input area component with file attachment, text input, and send button.
 * Extracted from ChatInner.v2.tsx.
 */

import { FC, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"
import Message from "src/app/models/Message"
import { Collaboration } from "src/app/models/Chat"
import { Order } from "src/app/models/Order"
import Language from "src/app/models/Language"
import ChatMessageToReply from "src/app/layout/chat-handle/components/chat-replymessage/ChatMessageToReply"
import { ChatMessageOrderInitiateView } from "src/app/layout/chat-handle/components/chat-orderinitiate/ChatMessageOrderInitiateView"
import { InteractiveMessageList } from "./InteractiveMessageList"
import FileTypeSelector from "./FileTypeSelector"

export interface ChatInputAreaProps {
  // State
  message: string
  file: File | undefined
  inputFileName: string
  messageType: string | undefined
  isSending: boolean
  replyToMessage: Message | ""
  orderInitiateMessage: Order | undefined

  // Collaboration data
  collaboration: Collaboration | undefined
  companyId: string
  /** Channel type for file restrictions (e.g., "whatsapp", "shopee") */
  channel?: string

  // Callbacks
  onMessageChange: (value: string) => void
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveFile: () => void
  onSendMessage: (templateLanguage?: Language) => void
  onEnterPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  onClearReply?: () => void
  onClearOrder?: () => void
}

const ChatInputArea: FC<ChatInputAreaProps> = ({
  message,
  file,
  inputFileName,
  messageType,
  isSending,
  replyToMessage,
  orderInitiateMessage,
  collaboration,
  companyId,
  channel,
  onMessageChange,
  onFileSelect,
  onRemoveFile,
  onSendMessage,
  onEnterPress,
  onClearReply,
  onClearOrder,
}) => {
  const { t } = useTranslation()
  const sendButtonRef = useRef<HTMLButtonElement>(null)
  const chatInputRef = useRef<HTMLTextAreaElement>(null)

  // Focus input on mount
  useEffect(() => {
    if (chatInputRef.current) {
      chatInputRef.current.focus()
    }
  }, [])

  // Handle text input change
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onMessageChange(e.target.value)
  }

  return (
    <div
      className="d-flex flex-column w-100 bg-secondary p-0 m-0 row-gap-1 px-2 py-2"
      style={{ flexShrink: 0 }} // Prevent footer from shrinking
      id="kt_chat_messenger_footer"
    >
      {/* Interactive message buttons */}
      <InteractiveMessageList
        companyId={companyId}
        sendMessage={onSendMessage}
        collaboration={collaboration}
      />

      {/* Order initiate message */}
      {!replyToMessage && orderInitiateMessage && (
        <div
          id="quoted-order-wrapper"
          data-testid="quoted-order-wrapper"
          className="d-flex flex-row align-items-center fade-in m-0 w-100 py-2 ps-3"
          style={{ backgroundColor: "#F0F3F7" }}
        >
          <div
            id="quoted-order"
            className="d-flex w-100"
            style={{
              margin: "auto",
              width: "100%",
              height: "auto",
              backgroundColor: "#C9C9C9",
              paddingRight: "0px",
              borderRadius: "8px",
              borderLeftWidth: "6px",
              borderLeftStyle: "solid",
              borderLeftColor: "#034CA3",
            }}
          >
            <ChatMessageOrderInitiateView order={orderInitiateMessage} />
          </div>
          <div
            className="d-flex align-items-center mx-3 cursor-pointer"
            style={{ right: 0 }}
          >
            <button
              type="button"
              className="btn btn-icon btn-sm btn-icon btn-active-light-primary m-0 p-0"
              onClick={onClearOrder}
            >
              <div className="d-flex align-items-center mx-3 bi bi-x-lg cursor-pointer" />
            </button>
          </div>
        </div>
      )}

      {/* Reply to message */}
      {replyToMessage !== "" && (
        <div
          id="quoted-reply-wrapper"
          data-testid="quoted-reply-wrapper"
          className="d-flex flex-row align-items-center fade-in m-0 w-100 py-2 ps-3"
        >
          <div
            id="quoted-reply"
            className="d-flex w-100"
            style={{
              margin: "auto",
              width: "100%",
              height: "auto",
              backgroundColor: "#C9C9C9",
              paddingRight: "0px",
              borderRadius: "8px",
              borderLeftWidth: "6px",
              borderLeftStyle: "solid",
              borderLeftColor: "#034CA3",
            }}
          >
            {replyToMessage &&
            (replyToMessage.messageType === "text" ||
              replyToMessage.messageType === "button" ||
              replyToMessage.messageType === "image" ||
              replyToMessage.messageType === "video" ||
              replyToMessage.messageType === "audio" ||
              replyToMessage.messageType === "document" ||
              replyToMessage.messageType === "location" ||
              replyToMessage?.messageType === "interactive") ? (
              <ChatMessageToReply
                messageToReply={replyToMessage}
                customer={collaboration?.customerModel}
                phoneNumber={collaboration?.phoneNumber ?? ""}
              />
            ) : (
              <div className="py-7 px-2" style={{ color: "#034CA3" }}>
                ONPROCESS
              </div>
            )}
          </div>
          <div
            className="d-flex align-items-center mx-3 cursor-pointer"
            style={{ right: 0 }}
          >
            <button
              type="button"
              className="btn btn-icon btn-sm btn-icon btn-active-light-primary m-0 p-0"
              onClick={onClearReply}
            >
              <div className="d-flex align-items-center mx-3 bi bi-x-lg cursor-pointer" />
            </button>
          </div>
        </div>
      )}

      {/* Input message row */}
      <div className="d-flex flex-row p-0 m-0 w-100">
        {/* File type selector / attachment button */}
        <div className="d-flex align-items-center me-3">
          {!file && (
            <FileTypeSelector
              channel={channel}
              onFileSelect={onFileSelect}
              disabled={isSending}
            />
          )}
          {file && (
            <>
              <label data-testid="file-uploaded-label" className="me-2">
                {inputFileName}
              </label>
              <button
                type="button"
                className="btn btn-sm btn-icon btn-light-danger"
                title={t("HC.File.RemoveFile")}
                onClick={onRemoveFile}
              >
                <i className="bi bi-x-circle fs-4" />
              </button>
            </>
          )}
        </div>

        {/* Text input */}
        <div
          className="bg-white w-100"
          style={{ overflowY: "scroll", borderRadius: "10px" }}
        >
          {messageType !== "document" && messageType !== "audio" && (
            <textarea
              data-testid="chatTextMessage"
              id="chatTextMessage"
              className="form-control form-control-flush px-2 py-auto"
              style={{
                minHeight: "40px",
                maxHeight: "120px",
                backgroundColor: "white",
                resize: "none", // Prevent manual resize
              }}
              maxLength={4096}
              rows={1}
              data-kt-element="input"
              placeholder={t("HC.Input.TextMessage")}
              value={message}
              onChange={handleTextChange}
              onKeyDown={onEnterPress}
              autoFocus
              ref={chatInputRef}
            />
          )}
        </div>

        {/* Send button */}
        <div className="d-flex align-items-center ms-3" style={{ right: 0 }}>
          <button
            id="sendButton"
            data-testid="send-button"
            ref={sendButtonRef}
            type="button"
            data-kt-element="send"
            className="btn btn-icon btn-sm btn-icon btn-active-light-primary m-0 p-0"
            onClick={() => onSendMessage()}
            disabled={isSending}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 14 14"
              className="text-primary"
            >
              <path
                fill="currentColor"
                fillRule="evenodd"
                d="M11.821.098a1.62 1.62 0 0 1 2.077 2.076l-3.574 10.712a1.62 1.62 0 0 1-1.168 1.069a1.599 1.599 0 0 1-1.52-.434l-1.918-1.909l-2.014 1.042a.5.5 0 0 1-.73-.457l.083-3.184l7.045-5.117a.625.625 0 1 0-.735-1.012L2.203 8.088l-1.73-1.73a1.6 1.6 0 0 1-.437-1.447a1.62 1.62 0 0 1 1.069-1.238h.003L11.82.097Z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

export default ChatInputArea
